import React, { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, ExternalLink, ShieldCheck } from 'lucide-react';
import { bookingsAPI } from '../../services/api';

/**
 * Shows a guest's ID document.
 *
 * The image is no longer a public URL — it is fetched through the authenticated
 * `/api/booking/:id/id-card` route and rendered from an in-memory object URL. That means
 * it cannot be hot-linked, does not appear in browser history or referrer headers, and
 * disappears from memory when the modal closes. Each view is recorded in the audit log.
 */
const IdCardViewer = ({ bookingId }) => {
  const [objectUrl, setObjectUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!bookingId) return undefined;

    let revoked = false;
    let url = null;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await bookingsAPI.getIdCard(bookingId);
        if (revoked) return;
        url = URL.createObjectURL(response.data);
        setObjectUrl(url);
      } catch (err) {
        if (revoked) return;
        setError(
          err.response?.status === 403
            ? 'You are not authorised to view this document.'
            : err.response?.status === 404
              ? 'The ID image is no longer on file.'
              : 'Could not load the ID document.'
        );
      } finally {
        if (!revoked) setLoading(false);
      }
    })();

    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [bookingId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-amber-700 py-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading ID document…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 text-sm text-red-600 py-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div>
      <a href={objectUrl} target="_blank" rel="noopener noreferrer" className="inline-block">
        <img
          src={objectUrl}
          alt="Guest ID document"
          className="w-full max-w-xs h-40 object-contain rounded-lg border border-amber-200 bg-white hover:shadow-lg transition-shadow cursor-pointer"
        />
      </a>
      <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
        <ExternalLink className="w-3 h-3" />
        Click to view full size
      </p>
      <p className="text-xs text-amber-500 mt-1 flex items-center gap-1">
        <ShieldCheck className="w-3 h-3" />
        This view is recorded in the audit log
      </p>
    </div>
  );
};

export default IdCardViewer;
